import { ModelContributionsSearch } from '@/dashboards/ModelContributions';
import { FilterControls } from '@/dashboards/ModelContributions/components/ActionBar/FilterControls';
import { FilterNode } from '@/dashboards/ModelContributions/components/ActionBar/Filter';
import { useModelContributionContext } from '@/dashboards/ModelContributions/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Drawer, useMatchMedia } from '@analytical-alley/ui';
import moment from 'moment/moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HiAdjustments } from 'react-icons/hi';

const STORAGE_KEY = 'modelContributionsSearch';

export const ActionBar = () => {
    const { isLg } = useMatchMedia(['lg']);
    const { search, setSearch } = useModelContributionContext();

    const { startDate, endDate } = useDashboardGlobalContext();

    const storedSearchString = window.sessionStorage.getItem(STORAGE_KEY);
    const storedSearch: ModelContributionsSearch = useMemo(
        () => (storedSearchString ? JSON.parse(storedSearchString) : {}),
        [storedSearchString],
    );

    const onSetSearch = (newSearch: Partial<ModelContributionsSearch>) => {
        setSearch(newSearch);
        window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                ...storedSearch,
                ...newSearch,
            }),
        );
    };

    const { contributionData, setSelectedNodes } =
        useModelContributionContext();

    const onSetSelectedNodes = (nodes: FilterNode[]) => {
        setSelectedNodes(nodes);
    };

    const nodes = useMemo(() => {
        return search.modelBreakdown === 'category'
            ? contributionData.treeNodes.map((node) => ({
                  ...node,
                  children: [],
              }))
            : contributionData.treeNodes;
    }, [contributionData.treeNodes, search.modelBreakdown]);

    const minDate = moment.utc(startDate, 'DD-MM-YYYY').toDate();
    const maxDate = moment.utc(endDate, 'DD-MM-YYYY').toDate();

    const canReset = useMemo(
        () =>
            (storedSearch.startDate && storedSearch.startDate !== startDate) ||
            (storedSearch.endDate && storedSearch.endDate !== endDate),
        [endDate, startDate, storedSearch.endDate, storedSearch.startDate],
    );

    useEffect(() => {
        if (search.startDate === startDate && storedSearch.startDate) {
            setSearch({
                startDate: storedSearch.startDate,
            });
        }

        if (search.endDate === endDate && storedSearch.endDate) {
            setSearch({
                endDate: storedSearch.endDate,
            });
        }
    }, [search, setSearch, startDate, endDate, storedSearch]);

    const onResetClick = useCallback(() => {
        window.sessionStorage.removeItem(STORAGE_KEY);
        setSearch({
            startDate: undefined,
            endDate: undefined,
            filter: undefined,
        });
    }, [setSearch]);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const controlProps = {
        search,
        nodes,
        minDate,
        maxDate,
        canReset: !!canReset,
        onSetSearch,
        onSetSelectedNodes,
        onResetClick,
    };

    if (!isLg) {
        return (
            <>
                <button
                    className="glass-tile self-center p-2 rounded-md shadow-lg hover:bg-white/30"
                    onClick={() => setIsDrawerOpen(true)}
                    aria-label="Open filters"
                >
                    <HiAdjustments className="w-5 h-5" />
                </button>

                <Drawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    position="top"
                >
                    <Drawer.Header title="Filters" />
                    <Drawer.Items>
                        <FilterControls {...controlProps} vertical />
                    </Drawer.Items>
                </Drawer>
            </>
        );
    }

    return <FilterControls {...controlProps} />;
};
